/** Formatting the console uses everywhere; numbers follow the person's locale. */

const nf = new Intl.NumberFormat(undefined);
const cf = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });

export const num = (n: number | null | undefined) => (n === null || n === undefined || Number.isNaN(n) ? "—" : nf.format(n));
export const compact = (n: number | null | undefined) => (n === null || n === undefined ? "—" : n < 10_000 ? nf.format(n) : cf.format(n));
export const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/** An amount with its currency code; without a known currency, the amount alone (never a guessed currency). */
export function money(n: number | null | undefined, currency = "", compactForm = false): string {
  if (n === null || n === undefined) return "—";
  const v = compactForm && Math.abs(n) >= 10_000 ? cf.format(n) : nf.format(Math.round(n));
  return currency ? `${currency} ${v}` : v;
}

export function bytes(n: number | null | undefined): string {
  if (!n && n !== 0) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
}

/** A stored time that means "not yet": empty, unreadable or before 1971 (a zero timestamp). */
export const never = (iso: string | null | undefined): boolean => {
  if (!iso) return true;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) || d.getUTCFullYear() < 1971;
};

/** "1 tenant", "3 tenants", "1,204 users"; the plural defaults to singular + "s". */
export const plural = (n: number, singular: string, pluralForm?: string): string => `${num(n)} ${n === 1 ? singular : (pluralForm ?? `${singular}s`)}`;

export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso || never(iso)) return "Never";
  const s = Math.round((now - new Date(iso).getTime()) / 1000);
  if (s < 0) return until(iso, now);
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return date(iso);
}

export function until(iso: string, now = Date.now()): string {
  const s = Math.round((new Date(iso).getTime() - now) / 1000);
  if (s <= 0) return "now";
  if (s < 3600) return `in ${Math.max(1, Math.round(s / 60))} min`;
  if (s < 86400) return `in ${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`;
  return `in ${Math.floor(s / 86400)} d`;
}

/** "35 min" past a deadline, or left before it. */
export function left(iso: string | null, now = Date.now()): { text: string; overdue: boolean } | null {
  if (!iso) return null;
  const s = Math.round((new Date(iso).getTime() - now) / 1000);
  const a = Math.abs(s);
  const t = a < 3600 ? `${Math.max(1, Math.round(a / 60))} min` : a < 86400 ? `${Math.floor(a / 3600)} h ${Math.round((a % 3600) / 60)} min` : `${Math.floor(a / 86400)} d`;
  return s < 0 ? { text: `Overdue ${t}`, overdue: true } : { text: `${t} left`, overdue: false };
}

export function date(iso: string | null | undefined, withTime = false): string {
  if (!iso) return "—";
  if (never(iso)) return "Never";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric", ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}) });
}

export function dateTime(iso: string | null | undefined): string {
  return date(iso, true);
}

export function duration(fromIso: string | null, toIso: string | null): string {
  if (!fromIso || !toIso) return "";
  const s = Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000);
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min ${s % 60} s`;
  return `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`;
}

export function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (!p.length) return "?";
  return p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

const TONES = ["#5B6BD6", "#B5524B", "#257A4C", "#8A6D1B", "#7A5AF8", "#0E7FA6", "#A44C86", "#4E7A2E", "#B0612B", "#3E6FA8"];
export function tone(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return TONES[Math.abs(h) % TONES.length];
}

export const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1).replace(/_/g, " ") : s);
